<script lang="ts">
	//Svelte hooks
	import { beforeUpdate } from 'svelte';
	//Stores
	import { user_settings } from 'src/stores/account/user';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { os } from 'src/stores/session/native-bridge';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	//Services
	import { format } from '../../../services/i18n';
	import { hapticFeedback } from '../../../services/native_container_requests_ios';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Helpers
	import { real_id } from '../../../helpers/interface_helpers';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';
	//Design system components
	import { Loader, WidgetCardMediaControls, toast } from '@Shelly/shelly-design-system';
	//Components
	import PinUnlock from '../../PinUnlock.svelte';
	import type { Request } from 'src/types/requests/request';
	import type { MediaResponseInterface } from 'src/types/MediaResponseInterface';
	import { hasControlRights } from 'src/helpers/user_helpers';

	export let device;

	$: statusStore = getDeviceStatus(device.id);
	$: status = $statusStore;
	$: isPlaying = status?.media?.playback?.enable;
	$: isLoading = status?.media?.playback?.buffering && !isPlaying;

	$: hasRights = hasControlRights(device);

	const channel = device?.shared ? device?.channel : undefined;
	let pinLocked;
	let showModal = false;

	let callbackValue;
	let playPreviousAfterUnlock = false;
	let playNextAfterUnlock = false;

	/**
	 * Handles the before update event of the component.
	 * Updates the pinLocked property based on the device pin lock status.
	 */
	beforeUpdate(() => {
		pinLocked =
			typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;
	});

	/**
	 * Handles error responses from API requests.
	 *
	 * @param {MediaResponseInterface} res - The error response from the API.
	 * @returns {void}
	 */
	const errorResponse = (res: MediaResponseInterface) => {
		/**
		 * The error code from the API response.
		 * @type {string}
		 */
		const code = res?.errors?.code.toString();

		/**
		 * The error message from the API response.
		 * @type {string}
		 */
		// const text = errors(code, res?.errors?.message);

		toast.open({ type: 'danger', title: code, text: res?.errors?.message });
	};

	/**
	 * Returns the i18n label for the given property.
	 * @param {string} prop - The property to get the label for.
	 * @returns {string} The i18n label for the property.
	 */
	const prop_i18n = (prop) => {
		switch (prop) {
			case 'brightness':
			case 'gain':
				return $format('label_brightness');
			case 'white':
				return $format('label_white_channel');
			default:
				return $format(`label_${prop}`);
		}
	};

	/**
	 * Handles the pin unlock event.
	 * Unlocks the pin and sends a request to update the volume.
	 */
	const handlePin = () => {
		// handleRequest(callbackValue);
		// using real id because thermostats id is _101 but we need not to change pin lock logic so if device has pin lock it's channel will have it also
		const _device = { ...device };
		pinLocked = unlockPin(_device, pinLocked);
		callbackValue = undefined;
	};

	/**
	 * Sends a request to toggle the play/pause state of the media player.
	 *
	 * @async
	 * @function togglePlay
	 * @returns {Promise<void>}
	 */
	const togglePlay = async () => {
		if ($os === 'IOS') {
			hapticFeedback('buttonPressed');
		}
		if (!pinLocked) {
			isLoading = true;
			const res: MediaResponseInterface = await gen2Request(
				device,
				'Media.MediaPlayer.PlayOrPause',
				{
					config: { channel },
				},
			);
			if (res.isok) {
				// getMediaStatus();
			} else {
				// errorResponse(res);
			}
			isLoading = false;
		} else {
			// callbackValue = val;
			showModal = true;
		}
	};

	/**
	 * Sends a request to play the previous media item.
	 *
	 * @async
	 * @function previous
	 * @returns {Promise<void>}
	 */
	const previous = async () => {
		if ($os === 'IOS') {
			hapticFeedback('buttonPressed');
		}
		if (!pinLocked) {
			const res: MediaResponseInterface = await gen2Request(device, 'Media.MediaPlayer.Previous', {
				config: { channel },
			});
			if (res.isok) {
				// getMediaStatus();
			} else {
				errorResponse(res);
				// getMediaStatus();
			}
		} else {
			playPreviousAfterUnlock = true;
			showModal = true;
		}
	};

	/**
	 * Sends a request to play the next media item.
	 *
	 * @async
	 * @function next
	 * @returns {Promise<void>}
	 */
	const next = async () => {
		if ($os === 'IOS') {
			hapticFeedback('buttonPressed');
		}
		if (!pinLocked) {
			const res: MediaResponseInterface = await gen2Request(device, 'Media.MediaPlayer.Next', {
				config: { channel },
			});
			if (res.isok) {
				// getMediaStatus();
			} else {
				errorResponse(res);
				// getMediaStatus();
			}
		} else {
			playNextAfterUnlock = true;
			showModal = true;
		}
	};
</script>

<div class="d-flex flex-direction-row align-items-center justify-content-center">
	<WidgetCardMediaControls
		{isLoading}
		{isPlaying}
		on:back-btn-click={(e) => {
			console.warn('back-btn-click', e);
			e.detail.stopPropagation();
			previous();
		}}
		on:next-btn-click={(e) => {
			e.detail.stopPropagation();
			next();
		}}
		on:play-pause-btn-click={(e) => {
			e.detail.stopPropagation();
			togglePlay();
		}}
		disabled={!hasRights}
	/>
</div>

{#if showModal}
	<PinUnlock bind:showModal on:unlock={handlePin} on:cancel={() => (showModal = false)} />
{/if}
